import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import HealthBadge, { documentHealth } from "@/components/HealthBadge";
import { Plus, X, Download, Trash2, Eye, Pencil, Upload as UploadIcon, FileText, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const EMP_DOC_TYPES = [
  "Führerschein", "Reisepass", "Personalausweis", "Arbeitsvertrag",
  "C95 Code 95 Karte", "ADR Schein", "Gesundheitsschein", "Sonstiges",
];
const VEH_DOC_TYPES = [
  "Zulassungsschein", "Versicherungspolizze", "§57a Pickerl", "§57a Gutachten",
  "Service", "Leasingvertrag", "Frachtbrief", "Sonstiges",
];
const BONUS_DOC_TYPES = [
  "Bescheid", "Anzeige", "Strafzettel", "Quittung", "Beleg", "Foto", "Sonstiges",
];
const CONTRACT_DOC_TYPES = [
  "Arbeitsvertrag", "Datenschutz-Vereinbarung", "Verschwiegenheitserklärung",
  "Zusatzvereinbarung", "Kündigung", "Dienstanweisung", "Schulungsnachweis", "Sonstiges",
];

/**
 * EmbeddedDocuments — inline document manager for owner-scoped documents.
 *
 * Shows + uploads + edits + downloads + deletes documents that belong to a single
 * owner (employee, vehicle, bonus, contract), embedded inside the owner's profile page.
 *
 * Props:
 *  - ownerType: "employee" | "vehicle" | "bonus" | "contract"
 *  - ownerId: string
 *  - ownerLabel: string — short human label shown in the empty/header area (e.g. "KO-858GR")
 *  - docTypes: optional override for the type dropdown (otherwise auto-resolved by ownerType)
 *  - title: optional override for the "+ Dokument" button label and modal heading
 *  - onChange: callback after CUD
 *  - compact: bool — render inline without big add button (for nested usage)
 */
export default function EmbeddedDocuments({ ownerType, ownerId, ownerLabel, onChange, docTypes, title, compact = false }) {
  const { can } = usePermissions();
  const canCreate = can("dokumente", "create");
  const canEdit = can("dokumente", "edit");
  const canDelete = can("dokumente", "delete");

  let resolvedTypes = docTypes;
  if (!resolvedTypes) {
    if (ownerType === "vehicle") resolvedTypes = VEH_DOC_TYPES;
    else if (ownerType === "bonus") resolvedTypes = BONUS_DOC_TYPES;
    else if (ownerType === "contract") resolvedTypes = CONTRACT_DOC_TYPES;
    else resolvedTypes = EMP_DOC_TYPES;
  }
  const docTypesList = resolvedTypes;
  const defaultDocType = docTypesList[0];

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const empty = {
    doc_type: defaultDocType, subject: "", description: "", expiry_date: "", file: null,
  };
  const [form, setForm] = useState(empty);
  const [dragOver, setDragOver] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrInfo, setOcrInfo] = useState(null);  // {confidence, owner_label}
  const dropRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/documents", { params: { owner_type: ownerType, owner_id: ownerId } });
      setDocs(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { if (ownerId) load(); /* eslint-disable-next-line */ }, [ownerType, ownerId]);

  function openCreate() {
    setEditing(null);
    setForm(empty);
    setFilePreview(null);
    setOcrInfo(null);
    setShowForm(true);
  }
  function openEdit(d) {
    setEditing(d);
    setForm({
      doc_type: d.doc_type || defaultDocType,
      subject: d.subject || "",
      description: d.description || "",
      expiry_date: d.expiry_date || "",
      file: null,
    });
    setFilePreview(null);
    setOcrInfo(null);
    setShowForm(true);
  }

  /** Run vision-OCR on the picked file and pre-fill the form fields. */
  async function runOcr(file) {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      // Non-image (e.g. PDF): no OCR, just keep file
      return;
    }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("file", file);
      const { data } = await api.post("/documents/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Pre-fill (only overwrite empty fields if user is editing)
      setForm((prev) => ({
        ...prev,
        doc_type: data.doc_type || prev.doc_type,
        subject: data.subject || prev.subject,
        expiry_date: data.expiry_date || prev.expiry_date,
        description: data.description || prev.description,
      }));
      setOcrInfo({ confidence: data.confidence, owner_label: data.owner_label });
      const pct = Math.round((data.confidence || 0) * 100);
      toast.success(`KI hat das Dokument erkannt (${pct}% sicher)`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Texterkennung fehlgeschlagen";
      toast.error(msg);
      setOcrInfo({ error: msg });
    } finally {
      setOcrBusy(false);
    }
  }

  function pickFile(file, { autoOcr = true } = {}) {
    setForm((f) => ({ ...f, file }));
    if (filePreview) URL.revokeObjectURL(filePreview);
    if (file && file.type?.startsWith("image/")) setFilePreview(URL.createObjectURL(file));
    else setFilePreview(null);
    setOcrInfo(null);
    if (autoOcr && file && file.type?.startsWith("image/")) {
      // run after state settles
      runOcr(file);
    }
  }
  function closeForm() {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setShowForm(false);
    setEditing(null);
    setForm(empty);
    setOcrInfo(null);
  }

  // Drag & drop on the form file zone
  function onDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragOver(true); }
  function onDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); }
  function onDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }

  async function save() {
    if (!form.subject.trim()) { toast.error("Bezeichnung ist Pflichtfeld"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("doc_type", form.doc_type);
      fd.append("subject", form.subject);
      fd.append("description", form.description || "");
      fd.append("expiry_date", form.expiry_date || "");
      if (form.file) fd.append("file", form.file);
      if (editing) {
        await api.put(`/documents/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Dokument aktualisiert");
      } else {
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Dokument hochgeladen");
      }
      closeForm();
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setUploading(false); }
  }

  async function remove(d) {
    if (!window.confirm(`Dokument "${d.subject}" wirklich löschen?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Gelöscht");
      load();
      onChange?.();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  }
  async function download(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.file_name || d.subject;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  }
  async function preview(d) {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch { toast.error("Vorschau fehlgeschlagen"); }
  }

  return (
    <div data-testid={`embedded-docs-${ownerType}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs text-zinc-500">
          {docs.length === 0 ? "Keine Dokumente erfasst" : `${docs.length} Dokument${docs.length === 1 ? "" : "e"}`}
          {ownerLabel && <span className="ml-1 text-zinc-600">· {ownerLabel}</span>}
        </div>
        {canCreate && (
          <button
            onClick={openCreate}
            data-testid={`embedded-doc-add-${ownerType}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded"
          >
            <Plus className="w-3.5 h-3.5"/> {title || "Dokument"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Lade…</div>
      ) : docs.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded p-8 text-center">
          <FileText className="w-8 h-8 text-zinc-600 mx-auto mb-2"/>
          <div className="text-sm text-zinc-400">Noch keine Dokumente.</div>
          {canCreate && (
            <button onClick={openCreate} className="mt-3 text-xs text-blue-400 hover:text-blue-300">
              Erstes Dokument hochladen →
            </button>
          )}
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-4 py-3 text-left">Typ</th>
                <th className="px-4 py-3 text-left">Bezeichnung</th>
                <th className="px-4 py-3 text-left">Datei</th>
                <th className="px-4 py-3 text-left">Ablauf</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const h = documentHealth(d);
                return (
                  <tr key={d.id} className="border-b border-zinc-800 hover:bg-zinc-800/40" data-testid={`embedded-doc-row-${d.id}`}>
                    <td className="px-4 py-2.5 text-zinc-300">{d.doc_type}</td>
                    <td className="px-4 py-2.5 text-zinc-100 font-medium">{d.subject}</td>
                    <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs truncate max-w-[200px]">
                      {d.file_name || <span className="italic text-zinc-600">— keine Datei —</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{fmtDate(d.expiry_date)}</td>
                    <td className="px-4 py-2.5"><HealthBadge level={h.level} reasons={h.reasons} size="xs" variant="badge" testid={`embedded-doc-health-${d.id}`}/></td>
                    <td className="px-4 py-2.5 text-right">
                      {d.file_name && (
                        <>
                          <button onClick={() => preview(d)} title="Vorschau" data-testid={`embedded-doc-preview-${d.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400"><Eye className="w-3.5 h-3.5"/></button>
                          <button onClick={() => download(d)} title="Download" data-testid={`embedded-doc-download-${d.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Download className="w-3.5 h-3.5"/></button>
                        </>
                      )}
                      {canEdit && <button onClick={() => openEdit(d)} title="Bearbeiten" data-testid={`embedded-doc-edit-${d.id}`} className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Pencil className="w-3.5 h-3.5"/></button>}
                      {canDelete && <button onClick={() => remove(d)} title="Löschen" data-testid={`embedded-doc-delete-${d.id}`} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">
                {editing ? `${title || "Dokument"} bearbeiten` : `Neues ${title || "Dokument"}`}
                {ownerLabel && <span className="ml-2 text-xs text-zinc-500 font-normal">· {ownerLabel}</span>}
              </h3>
              <button onClick={closeForm} className="text-zinc-400" data-testid="embedded-doc-form-close"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Typ</label>
                <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="embedded-doc-type-input"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1">
                  {docTypesList.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Ablaufdatum</label>
                <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500">Bezeichnung *</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="embedded-doc-subject-input"
                  className={`w-full px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 mt-1 ${!form.subject.trim() ? "border-red-500/40" : "border-zinc-800"}`}/>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500">Beschreibung</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-zinc-500 flex items-center justify-between">
                  <span>{editing ? "Datei ersetzen (optional)" : "Datei"}</span>
                  {form.file && form.file.type?.startsWith("image/") && (
                    <button
                      type="button"
                      onClick={() => runOcr(form.file)}
                      disabled={ocrBusy}
                      data-testid="embedded-doc-rerun-ocr"
                      className="inline-flex items-center gap-1 normal-case text-[10px] text-blue-400 hover:text-blue-300 disabled:opacity-50"
                    >
                      {ocrBusy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>}
                      {ocrBusy ? "Lese …" : "KI erneut auslesen"}
                    </button>
                  )}
                </label>
                {editing?.file_name && !form.file && (
                  <div className="mt-1 mb-2 text-xs text-zinc-500 inline-flex items-center gap-1.5">
                    <FileText className="w-3 h-3"/> Aktuelle Datei: <span className="font-mono text-zinc-300">{editing.file_name}</span>
                  </div>
                )}
                <div
                  ref={dropRef}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  data-testid="embedded-doc-dropzone"
                  className={`mt-1 rounded border border-dashed transition-colors ${
                    dragOver ? "border-blue-500 bg-blue-950/30" : "border-zinc-800 bg-zinc-950"
                  } px-4 py-5 text-center cursor-pointer`}
                  onClick={() => document.getElementById("embedded-doc-file-hidden")?.click()}
                >
                  <UploadIcon className="w-5 h-5 text-zinc-500 mx-auto mb-1.5"/>
                  <div className="text-xs text-zinc-300">
                    <span className="text-blue-400 font-medium">Datei hierher ziehen</span>
                    {" "}oder klicken zum Auswählen
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1">
                    JPG, PNG, WEBP — KI füllt Typ, Bezeichnung, Ablauf und Beschreibung automatisch aus
                  </div>
                  <input
                    id="embedded-doc-file-hidden"
                    type="file"
                    onChange={(e) => pickFile(e.target.files[0])}
                    data-testid="embedded-doc-file-input"
                    className="hidden"
                  />
                </div>
                {form.file && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-zinc-400">
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded border border-zinc-800"/>
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center bg-zinc-950 border border-zinc-800 rounded text-zinc-500 text-[10px] uppercase">
                        {(form.file.name.split(".").pop() || "FILE").toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-zinc-200 truncate">{form.file.name}</div>
                      <div className="text-zinc-500 text-[11px]">{(form.file.size / 1024).toFixed(1)} KB</div>
                      {ocrBusy && (
                        <div className="mt-1 text-blue-400 inline-flex items-center gap-1 text-[11px]" data-testid="embedded-doc-ocr-busy">
                          <Loader2 className="w-3 h-3 animate-spin"/> KI liest Dokument …
                        </div>
                      )}
                      {!ocrBusy && ocrInfo && !ocrInfo.error && typeof ocrInfo.confidence === "number" && (
                        <div className="mt-1 text-emerald-400 inline-flex items-center gap-1 text-[11px]" data-testid="embedded-doc-ocr-result">
                          <Sparkles className="w-3 h-3"/>
                          KI: {Math.round(ocrInfo.confidence * 100)}% sicher · Felder befüllt — manuell anpassbar
                        </div>
                      )}
                      {!ocrBusy && ocrInfo?.error && (
                        <div className="mt-1 text-red-400 text-[11px]" data-testid="embedded-doc-ocr-error">
                          {ocrInfo.error}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={closeForm} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded">Abbrechen</button>
              <button onClick={save} disabled={uploading} data-testid="embedded-doc-save-btn"
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium inline-flex items-center gap-1.5">
                <UploadIcon className="w-3.5 h-3.5"/>
                {uploading ? "Speichere…" : (editing ? "Aktualisieren" : "Hochladen")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
